// D23(d): a tela de Certificados organizada por titular, como a de credenciais.
// As pastas são as mesmas da página de credenciais (titulares, utils/holders.ts); aqui ficam só
// as regras próprias dos certificados: busca, titular sugerido, o que mudou na edição e a
// lembrança da visão (Pastas/Lista) no navegador.

import { Holder, SEM_TITULAR } from './holders';

/** "pastas" (por titular, padrão) ou "lista" (todos os certificados, como antes). */
export type OrganizacaoDosCertificados = 'pastas' | 'lista';

/** Chave própria: a escolha na tela de credenciais não muda a de certificados. */
export const ORGANIZACAO_CERTIFICADOS_KEY = 'lictor_certificates_organizacao';

/**
 * Visão lembrada no navegador. Guarda só "pastas" ou "lista" (nada do certificado).
 * Se o navegador bloquear o armazenamento (janela anônima, por exemplo), fica na visão Pastas.
 */
export function lerOrganizacao(): OrganizacaoDosCertificados {
  try {
    return window.localStorage.getItem(ORGANIZACAO_CERTIFICADOS_KEY) === 'lista' ? 'lista' : 'pastas';
  } catch {
    return 'pastas';
  }
}

export function guardarOrganizacao(organizacao: OrganizacaoDosCertificados): void {
  try {
    window.localStorage.setItem(ORGANIZACAO_CERTIFICADOS_KEY, organizacao);
  } catch {
    // Sem armazenamento: a escolha vale só enquanto a página estiver aberta.
  }
}

interface CertificadoParaBusca {
  label: string;
  holder_name?: string | null;
  subject_cn: string;
  issuer_cn: string;
}

/** Minúsculas e sem acentos: "joão" acha "JOAO" (o nome no certificado costuma vir sem acento). */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Busca da tela de Certificados: rótulo, titular (a pasta), nome no certificado e emissor.
 * Termo vazio combina com tudo.
 */
export function certificadoCombinaComBusca(certificado: CertificadoParaBusca, termo: string): boolean {
  const busca = normalizar(termo.trim());
  if (!busca) return true;
  return [certificado.label, certificado.holder_name || '', certificado.subject_cn, certificado.issuer_cn]
    .some((campo) => normalizar(campo).includes(busca));
}

/** "1 certificado" / "N certificados". */
export function quantidadeDeCertificados(quantidade: number): string {
  return `${quantidade} ${quantidade === 1 ? 'certificado' : 'certificados'}`;
}

/**
 * Titular já escolhido no cadastro feito de dentro de uma pasta aberta.
 * Vazio (= "Sem titular") fora de pasta, na pasta "Sem titular", ou se o titular da pasta não está
 * mais entre os ativos (arquivado): o servidor recusaria esse titular.
 */
export function titularSugerido(pastaAberta: string | null, holders: Pick<Holder, 'id'>[]): string {
  if (!pastaAberta || pastaAberta === SEM_TITULAR) return '';
  return holders.some((holder) => holder.id === pastaAberta) ? pastaAberta : '';
}

export interface AlteracoesDoCertificado {
  label?: string;
  /** null = "Sem titular" */
  holder_id?: string | null;
}

/**
 * Só o que mudou, no formato do PATCH /certificates/{id} (campo ausente = não mexe).
 * Rótulo vazio é enviado como vazio: o servidor volta ao nome que está no certificado.
 */
export function alteracoesDoCertificado(
  certificado: { label: string; holder_id: string | null },
  form: { label: string; holderId: string },
): AlteracoesDoCertificado {
  const alteracoes: AlteracoesDoCertificado = {};
  const rotulo = form.label.trim();
  if (rotulo !== certificado.label) alteracoes.label = rotulo;
  const titular = form.holderId || null;
  if (titular !== (certificado.holder_id || null)) alteracoes.holder_id = titular;
  return alteracoes;
}
